import type {
  AddWaitlistEntry,
  BookFromWaitlist,
  UpdateWaitlistEntry,
  WaitlistEntry,
  WaitlistFilters,
  WaitlistMatches,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The queue of patients wanting an earlier slot — **branch-scoped**.
 *
 * A waitlist entry names a chair-shaped need in one branch; somebody wanting an
 * earlier slot at two locations is two entries, because the slots that could
 * fill them are different slots.
 */

/**
 * Who is waiting, optionally narrowed to who could take a given slot.
 *
 * The filters are Anthony's QA item 13 — a queue of three is read at a glance
 * and a queue of forty is not. An entry with no preference of its own passes
 * every filter, because the unfussy patient is the most likely to say yes.
 */
export function listWaitlist(
  params: WaitlistFilters = {},
  signal?: AbortSignal,
): Promise<WaitlistEntry[]> {
  const query = new URLSearchParams();
  if (params.status !== undefined) query.set("status", params.status);
  if (params.weekday !== undefined) query.set("weekday", String(params.weekday));
  if (params.partOfDay !== undefined && params.partOfDay !== "ANY") {
    query.set("partOfDay", params.partOfDay);
  }
  if (params.from !== undefined) query.set("from", params.from);
  if (params.to !== undefined) query.set("to", params.to);

  const suffix = query.size === 0 ? "" : `?${query.toString()}`;
  return api<WaitlistEntry[]>(`/waitlist${suffix}`, { branchScoped: true, signal });
}

/**
 * Change a waiting patient's details, keeping their place in the queue.
 *
 * **There was no edit at all** (Anthony's QA, item 13). Correcting a duration
 * or adding the days somebody can now come meant cancelling and re-adding, and
 * the queue is ordered oldest-first — so a typo cost the patient their turn.
 */
export function updateWaitlistEntry(
  entryId: string,
  body: UpdateWaitlistEntry,
): Promise<WaitlistEntry> {
  return api<WaitlistEntry>(`/waitlist/${entryId}`, {
    method: "PATCH",
    body,
    branchScoped: true,
  });
}

/**
 * Who could fill the slot an appointment just freed.
 *
 * **Read fresh, never cached.** The answer is the calendar as it was one round
 * trip ago, and the whole point is that the slot only just came free.
 */
export function listWaitlistMatches(
  appointmentId: string,
  signal?: AbortSignal,
): Promise<WaitlistMatches> {
  return api<WaitlistMatches>(`/waitlist/matches?appointmentId=${appointmentId}`, {
    branchScoped: true,
    signal,
  });
}

export function addToWaitlist(body: AddWaitlistEntry): Promise<WaitlistEntry> {
  return api<WaitlistEntry>("/waitlist", { method: "POST", body, branchScoped: true });
}

/**
 * Take a waiting patient into the freed slot.
 *
 * **A refusal leaves them waiting**, which is the honest outcome: somebody took
 * the slot in between and this patient is still owed one.
 */
export function bookFromWaitlist(entryId: string, body: BookFromWaitlist): Promise<WaitlistEntry> {
  return api<WaitlistEntry>(`/waitlist/${entryId}/book`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

/** Take somebody off the queue without booking them. Kept, not deleted. */
export function cancelWaitlistEntry(entryId: string): Promise<WaitlistEntry> {
  return api<WaitlistEntry>(`/waitlist/${entryId}/cancel`, {
    method: "POST",
    branchScoped: true,
  });
}
